import http from '@/http'

export const SEARCH_FILTERS_RECEIVED = 'SEARCH_FILTERS_RECEIVED'
export const SEARCH_FILTERS_SET_SELECTED = 'SEARCH_FILTERS_SET_SELECTED'
export const SEARCH_FILTERS_CLEAR_SELECTED = 'SEARCH_FILTERS_CLEAR_SELECTED'
export const SEARCH_FILTERS_REMOVE_FILTER = 'SEARCH_FILTERS_REMOVE_FILTER'
export const SEARCH_FILTERS_UPDATE_SELECTED_OPTIONS = 'SEARCH_FILTERS_UPDATE_SELECTED_OPTIONS'
export const SEARCH_FILTERS_SET_SELECTED_AND_OPTIONS = 'SEARCH_FILTERS_SET_SELECTED_AND_OPTIONS'
export const SEARCH_FILTERS_CLEAR_QUERY = 'SEARCH_FILTERS_CLEAR_QUERY'
export const SEARCH_FILTERS_SET_QUERY = 'SEARCH_FILTERS_SET_QUERY'
export const SEARCH_FILTERS_CLEAR_TOC_QUERY = 'SEARCH_FILTERS_CLEAR_TOC_QUERY'
export const SEARCH_FILTERS_SET_TOC_QUERY = 'SEARCH_FILTERS_SET_TOC_QUERY'
export const SEARCH_FILTERS_CLEAR_BOM_QUERY = 'SEARCH_FILTERS_CLEAR_BOM_QUERY'
export const SEARCH_FILTERS_SET_BOM_QUERY = 'SEARCH_FILTERS_SET_BOM_QUERY'
export const SEARCH_FILTERS_CLEAR_PART_COLLECTION_QUERY = 'SEARCH_FILTERS_CLEAR_PART_COLLECTION_QUERY'
export const SEARCH_FILTERS_SET_PART_COLLECTION_QUERY = 'SEARCH_FILTERS_SET_PART_COLLECTION_QUERY'

const state = {
  items: [],
  isLoaded: false,
  selected: {},
  query: '',
  tocQuery: '',
  bomQuery: '',
  partCollectionQuery: ''
}

const getters = {
  selectedFilters: (state) => state.items.filter((filter) => state.selected[filter.field]),
  storedValueTagsMap: state => new Map(
    state.items.flatMap(it => {
      if (it.storeFilterValues && Object.hasOwn(it, 'values')) {
        return [[it.field, new Set(it.values.map(value => value.key))]]
      }
      return []
    })
  ),
  storedValueRangeTagsSet: state => new Set(
    state.items.flatMap(it => {
      if (it.storeFilterValues && !Object.hasOwn(it, 'values')) {
        return it.field
      }
      return []
    })
  ),
  isValidStoredValue: (state, getters) => (field, value) => {
    if (getters.storedValueRangeTagsSet.has(field)) {
      return true
    }
    else if (getters.storedValueTagsMap.has(field)) {
      return getters.storedValueTagsMap.get(field).has(value)
    }
    return false
  }
}

const actions = {
  async getFilters ({ commit }) {
    try {
      const { data } = await http.get('search/metadata')
      const dataWithSearchLabels = data.map(filter => {
        switch (filter.name) {
          case "%%type%%":
            return {
              ...filter,
              name: "%%searchType%%"
            }
          case "%%category%%":
            return {
              ...filter,
              name: "%%searchCategories%%"
            }
          default:
            return filter
        }
      })
      commit(SEARCH_FILTERS_RECEIVED, { filters: dataWithSearchLabels })
    } catch (err) {
      // na
    }
  },
  setSelectedFilters ({ commit }, selected) {
    commit(SEARCH_FILTERS_SET_SELECTED, { selected })
  },
  removeSelectedFilter ({ commit, dispatch }, filter ) {
    commit(SEARCH_FILTERS_REMOVE_FILTER, { filter })
    dispatch('navigateToSearch')
  },
  clearSelectedFilters ({ state, commit, dispatch, getters }) {
    const selectedFilters = state.items.flatMap(it => {
      const key = it.field
      const storedValue = localStorage.getItem("userSearchFilter_" + key)
      const retainValue = storedValue && getters.isValidStoredValue(key, storedValue)

      if (retainValue) {
        return it
      }
      localStorage.removeItem("userSearchFilter_" + key)
      localStorage.removeItem("userSearchFilter_" + key + "_suffix")
      localStorage.removeItem("userSearchFilter_" + key + "_prefix")
      return []
    })

    commit(SEARCH_FILTERS_SET_SELECTED, { selected: selectedFilters })
    dispatch('navigateToSearch')
  },
  updateSelectedFilterOptions ({ commit, dispatch }, { filter, options }) {
    commit(SEARCH_FILTERS_UPDATE_SELECTED_OPTIONS, { filter, options })
    let query
    const isPartNumber = filter.field === 'part_number'
    if (isPartNumber) {
      query = { input: options[0] }
    }
    dispatch('navigateToSearch', query)
  },
  setSelectedFiltersAndOptions ({ commit, dispatch }, options) {
    commit(SEARCH_FILTERS_SET_SELECTED_AND_OPTIONS, { options })
    dispatch('navigateToSearch')
  },
  clearSearchQuery({ commit }) {
    commit(SEARCH_FILTERS_CLEAR_QUERY)
  },
  setSearchQuery({ commit }, query) {
    commit(SEARCH_FILTERS_SET_QUERY, query)
  },
  clearTocQuery({ commit }) {
    commit(SEARCH_FILTERS_CLEAR_TOC_QUERY)
  },
  setTocQuery({ commit }, query) {
    commit(SEARCH_FILTERS_SET_TOC_QUERY, query)
  },
  clearBomQuery({ commit }) {
    commit(SEARCH_FILTERS_CLEAR_BOM_QUERY)
  },
  setBomQuery({ commit }, query) {
    commit(SEARCH_FILTERS_SET_BOM_QUERY, query)
  },
  clearPartCollectionQuery({ commit }) {
    commit(SEARCH_FILTERS_CLEAR_PART_COLLECTION_QUERY)
  },
  setPartCollectionQuery({ commit }, query) {
    commit(SEARCH_FILTERS_SET_PART_COLLECTION_QUERY, query)
  }
}

const mutations = {
  [SEARCH_FILTERS_RECEIVED] (state, { filters }) {
    state.items = filters
    state.isLoaded = true
    //if we saved out filter fields these need to be restored as selected fields on entering screen
    state.items.forEach((filter) => {
      const savedField = localStorage.getItem("userSearchFilter_" + filter.field)
      if (savedField) {
        state.selected[filter.field] = true
      }
      if (filter.type === 'range') {
        const prefix = `${filter.field}_prefix`
        const prefixValue = localStorage.getItem("userSearchFilter_" + prefix)
        if (prefixValue?.length > 0) {
          state.selected[prefix] = prefixValue
        }
        const suffix = `${filter.field}_suffix`
        const suffixValue = localStorage.getItem("userSearchFilter_" + suffix)
        if (suffixValue?.length > 0) {
          state.selected[suffix] = suffixValue
        }
      }
    })
  },
  [SEARCH_FILTERS_SET_SELECTED] (state, { selected }) {
    const filterFields = selected.map((filter) => filter.field)
    const selectedFilters = {}
    filterFields.forEach((field) => {
      selectedFilters[field] = state.selected[field]
        ? state.selected[field]
        : []
    })
    state.selected = selectedFilters
    //look for any others that were previously saved
    state.items.forEach((filter) => {
      if (!state.selected[filter.field]) {
        const savedField = localStorage.getItem("userSearchFilter_" + filter.field)
        if (savedField) {
          state.selected[filter.field] = true
          if (filter.type === 'range') {
            const prefix = `${filter.field}_prefix`
            const prefixValue = localStorage.getItem("userSearchFilter_" + prefix)
            if (prefixValue?.length > 0) {
              state.selected[prefix] = prefixValue
            }
            const suffix = `${filter.field}_suffix`
            const suffixValue = localStorage.getItem("userSearchFilter_" + suffix)
            if (suffixValue?.length > 0) {
              state.selected[suffix] = suffixValue
            }
          }
        }
      }
    })
  },
  [SEARCH_FILTERS_CLEAR_SELECTED] (state) {
    const selectedFilters = {}
    Object.keys(state.selected).forEach((key) => {
      selectedFilters[key] = []
      localStorage.removeItem("userSearchFilter_" + key)
      localStorage.removeItem("userSearchFilter_" + key + "_suffix")
      localStorage.removeItem("userSearchFilter_" + key + "_prefix")
    })
    state.selected = selectedFilters
  },
  [SEARCH_FILTERS_REMOVE_FILTER] (state, { filter }) {
    if (filter.type === 'range') {
      // Vue.delete(state.selected, `${filter.field}_prefix`)
      // Vue.delete(state.selected, `${filter.field}_suffix`)
      delete state.selected[`${filter.field}_prefix`]
      delete state.selected[`${filter.field}_suffix`]
    }
    // Vue.delete(state.selected, filter.field)
    delete state.selected[filter.field]
    // Range tags must be cleared here.
    // Other tags are cleared in SEARCH_FILTERS_SET_SELECTED_AND_OPTIONS
    localStorage.removeItem("userSearchFilter_" + filter.field)
    localStorage.removeItem("userSearchFilter_" + filter.field + "_suffix")
    localStorage.removeItem("userSearchFilter_" + filter.field + "_prefix")
    if (filter.pin)
      filter.pin = false
  },
  [SEARCH_FILTERS_UPDATE_SELECTED_OPTIONS] (state, { filter, options }) {
    if (filter.type === 'range') {
      const value = options[0]
      state.selected[filter.field] = value.value
      if (!value.value) {
        localStorage.removeItem("userSearchFilter_" + filter.field)
        localStorage.removeItem("userSearchFilter_" + filter.field + "_suffix")
        localStorage.removeItem("userSearchFilter_" + filter.field + "_prefix")
      }
      if (value.prefix) {
        state.selected[`${filter.field}_prefix`] = value.prefix
      } else if (value.suffix) {
        state.selected[`${filter.field}_suffix`] = value.suffix
      }
    } else {
      state.selected[filter.field] = options
    }
  },
  [SEARCH_FILTERS_SET_SELECTED_AND_OPTIONS] (state, { options }) {
    const query = {}
    Object.keys(state.selected).forEach((key) => {
      const selectedValues = state.selected[key]
      if (!selectedValues.length) {
        query[key] = []
      } else {
        query[key] = selectedValues
      }
    })
    Object.keys(options).forEach((key) => {
      const value = options[key]
      const values = Array.isArray(value) ? value : value && value.length ? [value] : []; // eslint-disable-line
      const filter = state.items.find((f) => f.field === key)
      if (filter) {
        if (filter.storeFilterValues) {
          if (values?.length > 0) {
            localStorage.setItem("userSearchFilter_" + key, values)
          }
          else if (filter.type !== 'range') {
            // Range tags are cleared in SEARCH_FILTERS_REMOVE_FILTER
            localStorage.removeItem("userSearchFilter_" + key)
            localStorage.removeItem("userSearchFilter_" + key + "_suffix")
            localStorage.removeItem("userSearchFilter_" + key + "_prefix")
          }
        }
        if (filter.type === 'range') {
          query[key] = values
          const prefix = `${key}_prefix`
          if (options[prefix]) {
            query[prefix] = options[prefix]
            if (filter.storeFilterValues && values?.length > 0) {
              localStorage.setItem("userSearchFilter_" + prefix, query[prefix])
            }
          }
          const suffix = `${key}_suffix`
          if (options[suffix]) {
            query[suffix] = options[suffix]
            if (filter.storeFilterValues && values?.length > 0) {
              localStorage.setItem("userSearchFilter_" + suffix, query[suffix])
            }
          }
        } else {
          query[key] = values
        }
      } else if (key === 'q') {
        state.query = options[key]
      }
    })
    state.items.forEach((filter) => {
      if ((filter.pin) && (!query[filter.field])) {
        query[filter.field] = []
      }
    })
    Object.keys(query).forEach((key) => {
      const filter = state.items.find((f) => f.field === key)
      if (filter && filter.storeFilterValues) {
        const savedValue = localStorage.getItem("userSearchFilter_" + key)
        if (query[key].length === 0 && savedValue !== null && savedValue.length > 0) {
          query[key] = savedValue.split(",")
        }
        if (filter.type === 'range') {
          const prefix = `${key}_prefix`
          const prefixValue = localStorage.getItem("userSearchFilter_" + prefix)
          if (query[prefix]?.length === 0 && prefixValue?.length > 0) {
            query[prefix] = prefixValue
          }
          const suffix = `${key}_suffix`
          const suffixValue = localStorage.getItem("userSearchFilter_" + suffix)
          if (query[suffix]?.length === 0 && suffixValue?.length > 0) {
            query[suffix] = suffixValue
          }
        }
      }
    })
    state.selected = query
  },
  [SEARCH_FILTERS_CLEAR_QUERY] (state) {
    state.query = ''
  },
  [SEARCH_FILTERS_SET_QUERY] (state, query) {
    state.query = query
  },
  [SEARCH_FILTERS_CLEAR_TOC_QUERY] (state) {
    state.tocQuery = ''
  },
  [SEARCH_FILTERS_SET_TOC_QUERY] (state, query) {
    state.tocQuery = query
  },
  [SEARCH_FILTERS_CLEAR_BOM_QUERY] (state) {
    state.bomQuery = ''
  },
  [SEARCH_FILTERS_SET_BOM_QUERY] (state, query) {
    state.bomQuery = query
  },
  [SEARCH_FILTERS_CLEAR_PART_COLLECTION_QUERY] (state) {
    state.partCollectionQuery = ''
  },
  [SEARCH_FILTERS_SET_PART_COLLECTION_QUERY] (state, query) {
    state.partCollectionQuery = query
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
